import {Controller} from '@hotwired/stimulus';
import {smoothScrollTo} from '../lib/smooth_scroll.js';

export default class extends Controller {
    connect() {
        this.initialHashHandled = false;
        this.firstFrame = null;
        this.secondFrame = null;
        this.handleInitialHash = this.handleInitialHash.bind(this);

        if (!window.location.hash) {
            return;
        }

        if (document.readyState === 'complete') {
            this.handleInitialHash();
            return;
        }

        window.addEventListener('load', this.handleInitialHash, {once: true});
    }

    disconnect() {
        window.removeEventListener('load', this.handleInitialHash);

        if (this.firstFrame !== null) {
            window.cancelAnimationFrame(this.firstFrame);
        }

        if (this.secondFrame !== null) {
            window.cancelAnimationFrame(this.secondFrame);
        }
    }

    handleInitialHash() {
        if (this.initialHashHandled) {
            return;
        }

        this.initialHashHandled = true;
        const target = this.findTarget(window.location.hash);

        if (!target) {
            return;
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion) {
            smoothScrollTo(this.targetTop(target));
            return;
        }

        window.scrollTo({top: 0, behavior: 'auto'});
        this.firstFrame = window.requestAnimationFrame(() => {
            this.secondFrame = window.requestAnimationFrame(() => {
                smoothScrollTo(this.targetTop(target));
            });
        });
    }

    scroll(event) {
        const link = event.target.closest('a[href*="#"]');

        if (!link || link.classList.contains('skip-link') || link.target === '_blank') {
            return;
        }

        const url = new URL(link.href, window.location.href);

        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) {
            return;
        }

        const target = this.findTarget(url.hash);

        if (!target) {
            return;
        }

        event.preventDefault();
        const top = this.targetTop(target);

        smoothScrollTo(top).then(() => {
            window.history.pushState(
                window.history.state,
                '',
                `${window.location.pathname}${window.location.search}${url.hash}`,
            );
        });
    }

    findTarget(hash) {
        try {
            return document.getElementById(decodeURIComponent(hash.slice(1)));
        } catch {
            return null;
        }
    }

    targetTop(target) {
        return Math.max(0, target.getBoundingClientRect().top + window.scrollY - 24);
    }
}
